import {
  useCallback,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { FormattedMessage } from 'react-intl';

import { formatBytes } from '@xing-com/crate-jobs-domain-apply-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import type { NwtTrackingProps } from '@xing-com/crate-jobs-tracking';
import { IconUpload } from '@xing-com/icons';

import * as S from './document-upload.styles';
import { FileItem } from './file-item';
import {
  UPLOAD_ERROR_COPY_KEYS,
  DEFAULT_MAX_FILESIZE_BYTES,
  DEFAULT_ACCEPTED_MIME_TYPES,
  getSupportedFileTypes,
  isSupportedMimeType,
  MAX_FILE_NAME_LENGTH,
} from './helpers';
import type { FileUpload, ErrorMsgType } from './types';

type Props = {
  origin?: string;
  onUploadComplete: (
    fileUploaded: FileUpload & { fileServerId: string }
  ) => void;
  onUploadDiscarded: () => void;
  maxSizeInBytes?: number;
  supportedTypes?: string;
  fileUploaded?: FileUpload;
  uploadFile: (file: File) => void;
  nwtProps: Omit<NwtTrackingProps, 'event' | 'eventSchema'>;
};

export const DocumentUpload: React.FC<Props> = ({
  origin = 'custom_field',
  onUploadComplete,
  onUploadDiscarded,
  maxSizeInBytes = DEFAULT_MAX_FILESIZE_BYTES,
  supportedTypes = DEFAULT_ACCEPTED_MIME_TYPES,
  fileUploaded,
  uploadFile,
  nwtProps,
}) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const trackNwt = useJobsNwtTracking();
  const [uploadError, setUploadError] = useState<ErrorMsgType | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const tipId = useId();
  const [successBannerCopy, setSuccessBannerCopy] = useState('');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [statusBannerFileName, setStatusBannerFileName] = useState('');

  const handleFiles = useCallback(
    (files: FileList | File[]): void => {
      if (files.length === 0) return;

      if (files.length > 1) {
        setUploadError('MAX_FILES_ALLOWED');
        trackNwt({
          eventSchema: 'basic',
          event: 'error_shown',
          element: `${origin}_upload_error_max_file_number_exceeded`,
          elementDetail: 'document_hub_upload_modal',
          ...nwtProps,
        });
      } else {
        const file = files[0];

        if (file.name.length > MAX_FILE_NAME_LENGTH) {
          setUploadError('FILE_NAME_TOO_LONG');
          trackNwt({
            eventSchema: 'basic',
            event: 'error_shown',
            element: `${origin}_upload_error_file_name_length_exceeded`,
            elementDetail: 'document_hub_upload_modal',
            ...nwtProps,
          });
        } else if (file.size > maxSizeInBytes) {
          setUploadError('FILE_SIZE_TOO_LARGE');
          trackNwt({
            eventSchema: 'basic',
            event: 'error_shown',
            element: `${origin}_upload_error_file_size_exceeded`,
            elementDetail: 'document_hub_upload_modal',
            ...nwtProps,
          });
        } else if (
          !file.type ||
          !isSupportedMimeType(file.type, supportedTypes)
        ) {
          setUploadError('MIME_TYPE_NOT_SUPPORTED');
          trackNwt({
            eventSchema: 'basic',
            event: 'error_shown',
            element: `${origin}_upload_error_invalid_file_type`,
            elementDetail: 'document_hub_upload_modal',
            ...nwtProps,
          });
        } else {
          uploadFile(file);
          trackNwt({
            eventSchema: 'basic',
            event: 'uploaded',
            element: origin,
            elementDetail: 'document_hub_upload_modal',
            ...nwtProps,
          });
        }
      }
    },
    [maxSizeInBytes, nwtProps, origin, supportedTypes, trackNwt, uploadFile]
  );

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (event.target.files !== null) {
        handleFiles(event.target.files);
      }
      event.target.value = '';
    },
    [handleFiles]
  );

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (event.dataTransfer.types.includes('Files')) {
      event.preventDefault();
      setIsDraggingFile(true);
    }
  }, []);

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (event.dataTransfer.types.includes('Files')) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget;

    if (
      !relatedTarget ||
      !(relatedTarget instanceof Node) ||
      !event.currentTarget.contains(relatedTarget)
    ) {
      setIsDraggingFile(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingFile(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles]
  );

  const onUploadButtonClick = useCallback((): void => {
    if (inputFileRef.current) {
      inputFileRef.current.click();

      trackNwt({
        eventSchema: 'basic',
        event: 'clicked',
        element: `upload_${origin}`,
        elementDetail: 'document_hub_upload_modal',
        ...nwtProps,
      });
    }
  }, [nwtProps, origin, trackNwt]);

  const onDeleteClick = useCallback(() => {
    onUploadDiscarded();
    trackNwt({
      eventSchema: 'basic',
      event: 'deleted',
      element: `saved_${origin}`,
      elementDetail: 'document_hub_upload_modal',
      ...nwtProps,
    });

    setSuccessBannerCopy('JOBS_DOCUMENT_HUB_MODAL_DELETE_SUCCESS');
    setShowSuccessBanner(true);
    setStatusBannerFileName(fileUploaded?.file.name ?? '');

    if (fileUploaded?.cancelUpload) {
      fileUploaded.cancelUpload();
    }
  }, [onUploadDiscarded, trackNwt, origin, nwtProps, fileUploaded]);

  const fileTypes = useMemo(
    () => getSupportedFileTypes(supportedTypes),
    [supportedTypes]
  );
  const maxSize = useMemo(() => formatBytes(maxSizeInBytes), [maxSizeInBytes]);

  useLayoutEffect(() => {
    if (
      fileUploaded?.status === 'loading-token' ||
      fileUploaded?.status === 'uploading'
    ) {
      setUploadError(null); // remove pending error banner on new upload
    } else if (fileUploaded?.status === 'error') {
      setUploadError(fileUploaded.error ?? 'GENERIC');
      trackNwt({
        eventSchema: 'basic',
        event: 'error_shown',
        element: `${origin}_upload_error_network`,
        elementDetail: 'document_hub_upload_modal',
        ...nwtProps,
      });
    } else if (
      fileUploaded?.status === 'success' &&
      fileUploaded?.fileServerId
    ) {
      setSuccessBannerCopy('JOBS_DOCUMENT_HUB_MODAL_UPLOAD_SUCCESS');
      setShowSuccessBanner(true);
      setStatusBannerFileName(fileUploaded.file.name);
      onUploadComplete({
        ...fileUploaded,
        fileServerId: fileUploaded.fileServerId,
      });
    }
  }, [fileUploaded, nwtProps, onUploadComplete, origin, trackNwt]);

  const hideButton =
    fileUploaded?.status === 'loading-token' ||
    fileUploaded?.status === 'uploading' ||
    fileUploaded?.status === 'success';

  return (
    <>
      {hideButton ? null : (
        <S.UploadWrapper
          $isDraggingFile={isDraggingFile}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <S.StyledIcon width={32} height={32} aria-hidden />
          <S.InfoWrapper>
            <S.UploadCopy>
              <strong>
                <FormattedMessage id="JOBS_DOCUMENT_HUB_MODAL_DRAG_DOCUMENT" />
              </strong>
            </S.UploadCopy>
            <S.FileFormatTip size="xsmall" noMargin>
              <FormattedMessage
                id="JOBS_DOCUMENT_HUB_MODAL_UPLOAD_HINT"
                values={{ maxSize, fileTypes }}
              />
            </S.FileFormatTip>
          </S.InfoWrapper>
          <S.UploadButton
            icon={IconUpload}
            size="medium"
            variant="secondary"
            data-testid="upload-button"
            onClick={onUploadButtonClick}
            aria-describedby={tipId}
          >
            <FormattedMessage id="JOBS_CUSTOM_APPLY_FORM_STEP_3_UPLOAD_FILE_BUTTON" />
          </S.UploadButton>
          <S.FileFormatTipMobile size="xsmall" noMargin>
            <FormattedMessage
              id="JOBS_DOCUMENT_HUB_MODAL_UPLOAD_HINT"
              values={{ maxSize, fileTypes }}
            />
          </S.FileFormatTipMobile>
          <S.AriaMessage id={tipId}>
            <FormattedMessage
              id="JOBS_DOCUMENT_HUB_MODAL_UPLOAD_HINT"
              values={{ maxSize, fileTypes }}
            />
          </S.AriaMessage>

          <input
            hidden
            type="file"
            accept={supportedTypes}
            ref={inputFileRef}
            onChange={onFileInputChange}
            data-testid={`upload-input-${origin}`}
          />
        </S.UploadWrapper>
      )}

      <S.ErrorBanner
        aria-live="polite"
        display="inline"
        variant="error"
        show={!!uploadError}
        handleOnClose={() => setUploadError(null)}
      >
        {uploadError ? (
          <FormattedMessage
            id={UPLOAD_ERROR_COPY_KEYS[uploadError]}
            values={{ maxFiles: 1, maxSize, fileTypes }}
          />
        ) : null}
      </S.ErrorBanner>

      <S.StatusBanner
        display="fixed"
        handleOnClose={() => {
          setShowSuccessBanner(false);
        }}
        show={showSuccessBanner}
        variant="success"
      >
        <FormattedMessage
          id={successBannerCopy}
          values={{ name: statusBannerFileName }}
        />
      </S.StatusBanner>

      <FileItem fileUpload={fileUploaded} onDelete={onDeleteClick} />

      <S.AriaMessage aria-live="polite">
        {fileUploaded?.status === 'success' ? (
          <FormattedMessage
            key={fileUploaded.fileServerId}
            id="JOBS_INSTANT_APPLY_UPLOAD_FILE_SUCCESS_A11Y"
            values={{ fileName: fileUploaded.file.name }}
          />
        ) : null}
      </S.AriaMessage>
    </>
  );
};
